import React from "react";
import {
  getAthleteName, getAthleteInstitution, getAthleteInstitutionLogo,
  getBracketStatusProps, getRoundLabel, groupBracketsByRound, getRankMedal, getVictoryTypeProps,
} from "../categorias.utils";
import InstitutionLogo   from "../InstitutionLogo";
import WrestlingRankingTable from "../WrestlingRankingTable";

// ─── Estilos de medalla ───────────────────────────────────────────────────────

const MEDAL_STYLE = {
  1: { bg: "bg-amber-400/5",  border: "border-l-amber-400/50",  text: "text-amber-700",  ring: "bg-amber-400/10 border-amber-400/30"  },
  2: { bg: "bg-slate-400/5",  border: "border-l-slate-400/50",  text: "text-slate-500",  ring: "bg-slate-400/10 border-slate-400/30"  },
  3: { bg: "bg-amber-700/5",  border: "border-l-amber-700/40",  text: "text-amber-800",  ring: "bg-amber-700/10 border-amber-700/30"  },
};
const getMedalStyle = (rank) =>
  MEDAL_STYLE[rank] ?? { bg: "", border: "border-l-transparent", text: "text-gray-800", ring: "bg-gray-100 border-transparent" };

// ─── Helpers UWW para ranking de eliminación ──────────────────────────────────

const UWW_ROUND_LABEL_MAP = {
  "1": "1/8 Final", "2": "1/4 Final", "3": "1/2 Final",
  "4": "Final 1-2", "5": "Repechage",  "6": "Final 3-5",
};
const mapRoundLabel = (raw) => {
  if (raw == null) return "Sin ronda";
  const s = String(raw).trim();
  return UWW_ROUND_LABEL_MAP[s] ?? s;
};

const computeCP = (victoryType, isP1Winner) => {
  if (isP1Winner === null) return { cp1: 0, cp2: 0 };
  const give = (w, l) => isP1Winner ? { cp1: w, cp2: l } : { cp1: l, cp2: w };
  switch ((victoryType ?? "").toUpperCase()) {
    case "VFA": case "VCA": return give(5, 0);
    case "VSU":             return give(4, 0);
    case "VSU1":            return give(4, 1);
    case "VPO1": case "VPO": return give(3, 1);
    default:                return give(3, 0);
  }
};

const buildEliminationRankingRows = (brackets) => {
  // Solo brackets finalizados o con ganador
  const finished = brackets.filter(
    (b) => String(b.status ?? "").toLowerCase() === "finalizado"
          || b.winner?.registrationId
  );
  if (!finished.length) return [];

  const acc = new Map();

  const upsert = (regId, wrestler, inst, logoUrl) => {
    if (!acc.has(regId)) {
      acc.set(regId, {
        registrationId: regId, wrestler, inst, logoUrl,
        place: null, cp: 0, vt: 0, st: 0, tp: 0, tpGvn: 0,
      });
    }
    return acc.get(regId);
  };

  for (const b of finished) {
    const [p1Entry, p2Entry] = b.participants || [];
    if (!p1Entry || !p2Entry) continue;

    const p1Id = p1Entry.athlete?.registrationId;
    const p2Id = p2Entry.athlete?.registrationId;
    if (!p1Id || !p2Id) continue;

    const winnerRegId = b.winner?.registrationId ?? null;
    const isP1Winner  = winnerRegId === p1Id ? true : winnerRegId === p2Id ? false : null;
    const { cp1, cp2 } = computeCP(b.victoryType, isP1Winner);
    const vtype = (b.victoryType ?? "").toUpperCase();

    // Scores — soporta tanto b.scores.participant1.score como p1Entry.score
    const sc1 = Number(b.scores?.participant1?.score ?? p1Entry?.score) || 0;
    const sc2 = Number(b.scores?.participant2?.score ?? p2Entry?.score) || 0;

    const rowA = upsert(p1Id, getAthleteName(p1Entry.athlete), getAthleteInstitution(p1Entry.athlete), getAthleteInstitutionLogo(p1Entry.athlete));
    const rowB = upsert(p2Id, getAthleteName(p2Entry.athlete), getAthleteInstitution(p2Entry.athlete), getAthleteInstitutionLogo(p2Entry.athlete));

    rowA.cp    += cp1;  rowA.tp    += sc1;  rowA.tpGvn += sc2;
    rowB.cp    += cp2;  rowB.tp    += sc2;  rowB.tpGvn += sc1;

    if (isP1Winner !== null) {
      const winRow = isP1Winner ? rowA : rowB;
      if (vtype === "VFA" || vtype === "VCA") winRow.vt += 1;
      if (vtype === "VSU" || vtype === "VSU1") winRow.st += 1;
    }
  }

  // Asignar puestos desde rondas finales
  const final12 =
    finished.find(b => mapRoundLabel(b.round) === "Final 1-2") ??
    finished.find(b => mapRoundLabel(b.round) === "Final");

  if (final12) {
    const winId  = final12.winner?.registrationId;
    const loseId = final12.participants?.find(p => !p.isWinner && p.athlete?.registrationId !== winId)?.athlete?.registrationId;
    if (winId  && acc.has(winId))  acc.get(winId).place  = 1;
    if (loseId && acc.has(loseId)) acc.get(loseId).place = 2;
  }

  const finals35 = finished.filter(
    b => mapRoundLabel(b.round) === "Final 3-5" || mapRoundLabel(b.round) === "Final 3-4"
  );
  let nextBronze = 3;
  for (const bm of finals35) {
    const winId  = bm.winner?.registrationId;
    const loseId = bm.participants?.find(p => p.athlete?.registrationId !== winId)?.athlete?.registrationId;
    if (winId  && acc.has(winId)  && acc.get(winId).place  == null) acc.get(winId).place  = nextBronze;
    if (loseId && acc.has(loseId) && acc.get(loseId).place == null) acc.get(loseId).place = nextBronze + 2;
    nextBronze += 4;
  }

  const rows = Array.from(acc.values()).sort((a, b) => {
    const pa = a.place ?? 9999, pb = b.place ?? 9999;
    if (pa !== pb) return pa - pb;
    if (b.cp !== a.cp) return b.cp - a.cp;
    if (b.vt !== a.vt) return b.vt - a.vt;
    if (b.st !== a.st) return b.st - a.st;
    if (b.tp !== a.tp) return b.tp - a.tp;
    return a.tpGvn - b.tpGvn;
  });

  let next = (rows.filter(r => r.place != null).at(-1)?.place ?? 0) + 1;
  for (const r of rows) {
    if (r.place == null) r.place = next++;
  }

  return rows;
};

// ─── AthleteSlot ──────────────────────────────────────────────────────────────

const AthleteSlot = ({ entry, score, isWin }) => {
  const name    = getAthleteName(entry?.athlete);
  const inst    = getAthleteInstitution(entry?.athlete);
  const logoUrl = getAthleteInstitutionLogo(entry?.athlete);

  return (
    <div className={`flex items-center justify-between px-3 py-2 gap-2 transition-colors duration-150
      ${isWin ? "bg-green-500/[0.06]" : "bg-transparent"}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <InstitutionLogo logoUrl={logoUrl} name={inst} size={20} showTooltip={!!inst} />
        <div className="min-w-0">
          <p className={`text-[0.8rem] truncate ${isWin ? "font-bold text-green-600" : "font-medium text-gray-800"}`}>
            {name}
          </p>
          <p className="text-[0.67rem] text-gray-400 truncate block">{inst}</p>
        </div>
      </div>
      <div className={`min-w-[32px] h-[26px] flex items-center justify-center rounded flex-shrink-0
        ${isWin ? "bg-green-500/10" : "bg-gray-100"}`}
      >
        <span className={`text-[0.82rem] tabular-nums ${isWin ? "font-extrabold text-green-600" : "font-medium text-gray-500"}`}>
          {score ?? "—"}
        </span>
      </div>
    </div>
  );
};

// ─── MatchCard ────────────────────────────────────────────────────────────────

const MatchCard = ({ bracket }) => {
  const [p1Entry, p2Entry] = bracket.participants || [];
  const winnerRegId  = bracket.winner?.registrationId;
  const p1RegId      = p1Entry?.athlete?.registrationId;
  const p2RegId      = p2Entry?.athlete?.registrationId;
  const s1           = bracket.scores?.participant1?.score ?? p1Entry?.score;
  const s2           = bracket.scores?.participant2?.score ?? p2Entry?.score;
  const statusProps  = getBracketStatusProps(bracket.status);
  const victoryProps = bracket.victoryType ? getVictoryTypeProps(bracket.victoryType) : null;

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden w-full min-w-[220px] max-w-[300px]
                    hover:shadow-md hover:border-blue-300 transition-all duration-200">
      <div className="px-3 py-1.5 flex justify-between items-center bg-black/[0.03] border-b border-gray-200">
        <span className="text-[0.68rem] font-bold text-gray-400 tracking-wide">
          #{bracket.matchNumber}
        </span>
        <div className="flex items-center gap-1.5">
          {victoryProps && (
            <span
              className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded border ${victoryProps.color}`}
              title={victoryProps.title}
            >
              {victoryProps.label}
            </span>
          )}
          <span className="text-[0.6rem] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 tracking-wide">
            {statusProps.label}
          </span>
        </div>
      </div>
      <AthleteSlot entry={p1Entry} score={s1} isWin={winnerRegId === p1RegId} />
      <hr className="mx-3 border-dashed border-gray-200" />
      <AthleteSlot entry={p2Entry} score={s2} isWin={winnerRegId === p2RegId} />
    </div>
  );
};

// ─── VistaLlaves ──────────────────────────────────────────────────────────────

const VistaLlaves = ({ brackets }) => {
  const roundGroups = groupBracketsByRound(brackets);

  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex items-start gap-16 min-w-max px-2 py-4">
        {roundGroups.map(([round, roundBrackets]) => (
          <div key={round} className="flex flex-col items-center">
            <div className="text-center mb-3">
              <span className="text-[0.65rem] font-bold uppercase tracking-wide px-2.5 py-1 rounded bg-blue-600 text-white">
                {getRoundLabel(round)}
              </span>
            </div>
            <div
              className="flex flex-col justify-around"
              style={{ gap: roundBrackets.length > 1 ? "2rem" : "0.5rem" }}
            >
              {roundBrackets.map((bracket) => (
                <MatchCard key={bracket.bracketId ?? bracket.matchNumber} bracket={bracket} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── PodioFinal ───────────────────────────────────────────────────────────────

const PodioFinal = ({ podium }) => {
  const ranked = podium.filter((p) => p.rank != null).sort((a, b) => a.rank - b.rank);
  if (!ranked.length) return null;

  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-2.5 flex items-center gap-3 bg-black/[0.03] border-b border-gray-200">
        <div className="w-0.5 h-5 rounded-full bg-amber-500 flex-shrink-0" />
        <span className="text-[0.82rem] font-bold text-gray-800">Podio final</span>
      </div>
      <div className="flex flex-col">
        {ranked.map((entry, idx) => {
          const ms      = getMedalStyle(entry.rank);
          const medal   = getRankMedal(entry.rank);
          const name    = getAthleteName(entry.athlete?.athlete);
          const inst    = getAthleteInstitution(entry.athlete?.athlete);
          const logoUrl = getAthleteInstitutionLogo(entry.athlete);
          const isTop3  = entry.rank <= 3;

          return (
            <div
              key={entry.athlete?.registrationId ?? idx}
              className={`flex items-center gap-4 px-5 py-3 border-l-4 transition-colors
                ${idx < ranked.length - 1 ? "border-b border-gray-100" : ""}
                ${ms.bg} ${ms.border}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border
                ${isTop3 ? ms.ring : "bg-gray-100 border-transparent"}`}
              >
                {medal.emoji ? (
                  <span className="text-lg leading-none">{medal.emoji}</span>
                ) : (
                  <span className="text-[0.8rem] font-bold text-gray-400">{entry.rank}</span>
                )}
              </div>
              <InstitutionLogo logoUrl={logoUrl} name={inst} size={34} showTooltip={!!inst} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {entry.rank === 1 && <span className="text-amber-400 text-xs flex-shrink-0">🏆</span>}
                  <p className={`text-[0.88rem] truncate ${isTop3 ? `font-bold ${ms.text}` : "font-medium text-gray-800"}`}>
                    {name}
                  </p>
                </div>
                <p className="text-[0.7rem] text-gray-400 truncate">{inst}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── RoundTable ───────────────────────────────────────────────────────────────

const RoundTable = ({ roundBrackets }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm border-collapse" style={{ tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "38px" }} />
        <col style={{ width: "32%" }} />
        <col style={{ width: "90px" }} />
        <col style={{ width: "70px" }} />
        <col style={{ width: "28%" }} />
        <col style={{ width: "90px" }} />
      </colgroup>
      <thead>
        <tr className="bg-black/[0.02] border-b border-gray-200">
          <th className="pl-4 pr-2 py-2 text-left text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">#</th>
          <th className="px-3 py-2 text-left text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Atleta A</th>
          <th className="px-3 py-2 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Marcador</th>
          <th className="px-2 py-2 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Tipo</th>
          <th className="px-3 py-2 text-left text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Atleta B</th>
          <th className="pr-4 pl-2 py-2 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Estado</th>
        </tr>
      </thead>
      <tbody>
        {roundBrackets.map((bracket) => {
          const [p1Entry, p2Entry] = bracket.participants || [];
          const winnerRegId  = bracket.winner?.registrationId;
          const p1RegId      = p1Entry?.athlete?.registrationId;
          const p2RegId      = p2Entry?.athlete?.registrationId;
          const s1           = bracket.scores?.participant1?.score ?? p1Entry?.score ?? "—";
          const s2           = bracket.scores?.participant2?.score ?? p2Entry?.score ?? "—";
          const statusProps  = getBracketStatusProps(bracket.status);
          const hasWinner    = !!winnerRegId;
          const victoryProps = bracket.victoryType ? getVictoryTypeProps(bracket.victoryType) : null;

          return (
            <tr key={bracket.bracketId} className="border-b border-gray-100 hover:bg-black/[0.02] transition-colors">
              <td className="pl-4 pr-2 py-2.5 text-[0.72rem] text-gray-400">
                {bracket.matchNumber}
              </td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <InstitutionLogo logoUrl={getAthleteInstitutionLogo(p1Entry?.athlete)} name={getAthleteInstitution(p1Entry?.athlete)} size={18} showTooltip />
                  <div className="min-w-0">
                    <p className={`text-[0.8rem] truncate ${winnerRegId === p1RegId ? "font-bold text-green-600" : "font-medium text-gray-800"}`}>
                      {getAthleteName(p1Entry?.athlete)}
                    </p>
                    <p className="text-[0.67rem] text-gray-400 truncate">{getAthleteInstitution(p1Entry?.athlete)}</p>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2.5 text-center">
                <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg border
                  ${hasWinner ? "bg-green-500/[0.06] border-green-400/30" : "bg-gray-100 border-gray-200"}`}
                >
                  <span className={`text-[0.82rem] tabular-nums min-w-[18px] text-right
                    ${winnerRegId === p1RegId ? "font-extrabold text-green-600" : "font-medium text-gray-400"}`}>
                    {s1}
                  </span>
                  <span className="text-gray-300 text-[0.7rem] select-none">·</span>
                  <span className={`text-[0.82rem] tabular-nums min-w-[18px] text-left
                    ${winnerRegId === p2RegId ? "font-extrabold text-green-600" : "font-medium text-gray-400"}`}>
                    {s2}
                  </span>
                </div>
              </td>
              <td className="px-2 py-2.5 text-center">
                {victoryProps ? (
                  <span
                    className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded border ${victoryProps.color}`}
                    title={victoryProps.title}
                  >
                    {victoryProps.label}
                  </span>
                ) : (
                  <span className="text-gray-300 text-xs">—</span>
                )}
              </td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <InstitutionLogo logoUrl={getAthleteInstitutionLogo(p2Entry?.athlete)} name={getAthleteInstitution(p2Entry?.athlete)} size={18} showTooltip />
                  <div className="min-w-0">
                    <p className={`text-[0.8rem] truncate ${winnerRegId === p2RegId ? "font-bold text-green-600" : "font-medium text-gray-800"}`}>
                      {getAthleteName(p2Entry?.athlete)}
                    </p>
                    <p className="text-[0.67rem] text-gray-400 truncate">{getAthleteInstitution(p2Entry?.athlete)}</p>
                  </div>
                </div>
              </td>
              <td className="pr-4 pl-2 py-2.5 text-center">
                <span className="text-[0.62rem] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-500 tracking-wide">
                  {statusProps.label}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ─── VistaResultados ──────────────────────────────────────────────────────────

const VistaResultados = ({ brackets, podium, showWrestlingRanking }) => {
  const roundGroups   = groupBracketsByRound(brackets);
  const hasRank       = podium.some((p) => p.rank != null);
  const wrestlingRows = showWrestlingRanking ? buildEliminationRankingRows(brackets) : [];

  return (
    <div className="flex flex-col gap-6 p-1">
      {wrestlingRows.length > 0
        ? <WrestlingRankingTable rows={wrestlingRows} title="Clasificación Final — Fase Eliminación" />
        : hasRank && <PodioFinal podium={podium} />
      }

      {roundGroups.map(([round, roundBrackets]) => (
        <div key={round} className="rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-2.5 flex items-center gap-3 bg-black/[0.03] border-b border-gray-200">
            <div className="w-0.5 h-5 rounded-full bg-blue-600 flex-shrink-0" />
            <span className="text-[0.82rem] font-bold text-gray-800">{getRoundLabel(round)}</span>
            <span className="ml-auto text-[0.6rem] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-500 tracking-wide">
              {roundBrackets.length} combate{roundBrackets.length !== 1 ? "s" : ""}
            </span>
          </div>
          <RoundTable roundBrackets={roundBrackets} />
        </div>
      ))}
    </div>
  );
};

// ─── FaseEliminacion (export) ─────────────────────────────────────────────────

const FaseEliminacion = ({ fase, vista, showWrestlingRanking = false }) => {
  if (!fase?.brackets?.length) {
    return (
      <div className="m-4 flex items-start gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
        <span>ℹ️</span>
        <span>No hay combates registrados en esta fase.</span>
      </div>
    );
  }

  const podium = fase.podium ?? [];

  return vista === "resultados" ? (
    <VistaResultados brackets={fase.brackets} podium={podium} showWrestlingRanking={showWrestlingRanking} />
  ) : (
    <VistaLlaves brackets={fase.brackets} />
  );
};

export default FaseEliminacion;